import React, { useState } from 'react';
import Logo from '../Logo';

export default function RecordingScreen({
  courtId, courtName, playerEmail, elapsedDisplay, onStop,
}) {
  // Tapping Stop queues the stop command and returns the kiosk straight to
  // the "ready to record" screen — see useRecording.stopRecording. There is
  // no blocking "uploading/processing" screen here anymore; that work
  // happens entirely in the background and shows up as a small, dismissible
  // status pill (see BackgroundJobsBar) instead of taking over the kiosk.
  return (
    <div style={s.page}>
      <div style={s.topBar}>
        <span style={s.logo}><Logo size={22} style={{ marginRight: 8, verticalAlign: -6 }} />Picklers Lounge</span>
        <span style={s.courtChip}>{courtName}</span>
      </div>

      <div style={s.center}>
        <div style={s.card} className="fade-in">
          {/* Live badge */}
          <div style={s.liveBadge}>
            <span style={s.liveDot} />
            RECORDING LIVE
          </div>

          <LivePreview courtId={courtId} />

          {/* Timer */}
          <div style={s.timer}>{elapsedDisplay}</div>

          <p style={s.playerLabel}>
            Recording for <strong style={s.playerName}>{playerEmail}</strong>
          </p>

          {/* Stop — immediate, no confirmation step */}
          <button
            onClick={onStop}
            style={s.stopBtn}
            onMouseEnter={e => (e.currentTarget.style.background = '#dc2626')}
            onMouseLeave={e => (e.currentTarget.style.background = 'var(--red)')}
          >
            <span style={s.stopSquare} /> Stop Recording
          </button>

          <p style={s.hint}>Your video will be emailed automatically when you stop.</p>
        </div>
      </div>
    </div>
  );
}

/**
 * Best-effort live camera preview. Polls a lightweight JPEG snapshot
 * endpoint every 2.5s — never the video feed itself, and entirely
 * decoupled from FFmpeg (see recordings.views.CourtPreviewView). If the
 * onsite agent isn't pushing frames (no headroom, feature unsupported,
 * network hiccup) the endpoint just returns 204 and this quietly renders
 * nothing — it never blocks, retries aggressively, or otherwise risks
 * competing with the recording for resources.
 */
function LivePreview({ courtId }) {
  const [src, setSrc] = useState(null);
  const [failCount, setFailCount] = useState(0);

  React.useEffect(() => {
    if (!courtId || failCount >= 3) return; // give up quietly after 3 empty polls
    let cancelled = false;
    const API_BASE = process.env.REACT_APP_API_URL || '';
    const url = `${API_BASE}/api/recordings/courts/${courtId}/preview/`;

    const poll = async () => {
      try {
        const res = await fetch(url, { cache: 'no-store' });
        if (cancelled) return;
        if (res.status === 200) {
          const blob = await res.blob();
          setSrc(prev => {
            if (prev) URL.revokeObjectURL(prev);
            return URL.createObjectURL(blob);
          });
          setFailCount(0);
        } else {
          setFailCount(c => c + 1);
        }
      } catch {
        setFailCount(c => c + 1);
      }
    };

    poll();
    const id = setInterval(poll, 2500);
    return () => { cancelled = true; clearInterval(id); };
  }, [courtId, failCount]);

  if (!src) return null;

  return (
    <div style={s.previewWrap}>
      <img src={src} alt="Live camera preview" style={s.previewImg} />
      <span style={s.previewBadge}>LIVE PREVIEW</span>
      {/* Mirrors the watermark position baked into the final exported
          video (see recordings.video_processing on the backend) so what
          players see here matches what they'll get in their inbox. */}
      <Logo size={28} square style={s.previewLogo} />
    </div>
  );
}

const s = {
  page: {
    minHeight: '100vh', background: 'var(--gray-900)',
    display: 'flex', flexDirection: 'column',
    fontFamily: 'var(--font)',
  },
  topBar: {
    height: 60, padding: '0 28px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    background: 'rgba(255,255,255,0.05)',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
  logo: { fontSize: 17, fontWeight: 700, color: 'white' },
  courtChip: {
    background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.8)',
    fontSize: 13, fontWeight: 600,
    padding: '5px 14px', borderRadius: 50,
  },
  center: {
    flex: 1, display: 'flex',
    alignItems: 'center', justifyContent: 'center', padding: 24,
  },
  card: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 'var(--radius-lg)',
    padding: '48px 40px',
    width: '100%', maxWidth: 460,
    textAlign: 'center',
    backdropFilter: 'blur(20px)',
  },
  liveBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    background: 'rgba(239,68,68,0.15)',
    border: '1px solid rgba(239,68,68,0.3)',
    color: '#fca5a5',
    fontSize: 12, fontWeight: 700, letterSpacing: '0.1em',
    padding: '6px 14px', borderRadius: 50,
    marginBottom: 20,
  },
  liveDot: {
    width: 8, height: 8, borderRadius: '50%',
    background: 'var(--red)',
    animation: 'pulse-dot 1.2s ease-in-out infinite',
    display: 'inline-block',
  },
  previewWrap: {
    position: 'relative', marginBottom: 24,
    borderRadius: 12, overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.12)',
    lineHeight: 0,
  },
  previewImg: { width: '100%', display: 'block', maxHeight: 220, objectFit: 'cover' },
  previewBadge: {
    position: 'absolute', top: 8, left: 8,
    background: 'rgba(0,0,0,0.55)', color: 'white',
    fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
    padding: '3px 8px', borderRadius: 20,
  },
  previewLogo: {
    position: 'absolute', bottom: 8, right: 8,
    boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
    border: '1.5px solid rgba(255,255,255,0.8)',
  },
  timer: {
    fontSize: 88, fontWeight: 300, color: 'white',
    letterSpacing: '0.02em', fontVariantNumeric: 'tabular-nums',
    lineHeight: 1, marginBottom: 12,
  },
  playerLabel: {
    fontSize: 16, color: 'rgba(255,255,255,0.55)',
    marginBottom: 44,
  },
  playerName: { color: 'rgba(255,255,255,0.9)', fontWeight: 600 },
  stopBtn: {
    width: '100%', padding: '17px',
    background: 'var(--red)', color: 'white',
    border: 'none', borderRadius: 'var(--radius-sm)',
    fontSize: 17, fontWeight: 700,
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
    transition: 'background 0.15s', marginBottom: 20,
    boxShadow: '0 4px 20px rgba(239,68,68,0.35)',
    cursor: 'pointer',
    animation: 'rec-glow 2s ease-in-out infinite',
  },
  stopSquare: {
    width: 14, height: 14, borderRadius: 3,
    background: 'white', flexShrink: 0,
  },
  hint: {
    color: 'rgba(255,255,255,0.25)',
    fontSize: 13, lineHeight: 1.5,
  },
};
